import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ViewInArRoundedIcon from "@mui/icons-material/ViewInArRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { AI_ASSISTANT_BLUE } from "../constants";

function bulletsToText(bullets) {
  return (Array.isArray(bullets) ? bullets : []).join("\n");
}

function textToBullets(text) {
  return String(text || "")
    .split("\n")
    .map((line) => line.replace(/^[-•*]\s*/, "").trim())
    .filter(Boolean)
    .slice(0, 8);
}

function isMediaSlide(slide) {
  return (
    slide?.slide_kind === "media" ||
    slide?.is_media_slide === true ||
    (Boolean(slide?.related_3d?.modelUrl || slide?.related_3d?.imageUrl) &&
      !(Array.isArray(slide?.bullets) && slide.bullets.length > 0))
  );
}

/** True for a direct image file URL (title cover), not a deep-zoom tile root. */
function looksLikeStaticImageUrl(url) {
  const path = String(url || "")
    .split("?")[0]
    .split("#")[0];
  return /\.(png|jpe?g|webp|gif)$/i.test(path);
}

/**
 * Cover thumb for library media.
 * Never use a raw tile-root URL as <img src> — that shows a broken icon.
 */
function resolveLibraryThumb(media = {}) {
  if (media.thumbnailUrl) return String(media.thumbnailUrl);
  if (media.image && looksLikeStaticImageUrl(media.image)) return String(media.image);
  if (media.imageUrl && looksLikeStaticImageUrl(media.imageUrl)) {
    return String(media.imageUrl);
  }
  const tileSource = media.tileUrl || media.imageUrl;
  if (!tileSource) return media.image || null;
  if (String(tileSource).includes("{z}")) {
    return String(tileSource)
      .replace(/\/+$/, "")
      .replace(/\/\{z\}\/\{x\}\/\{y\}\.[a-z0-9]+$/i, "/3/3/3.png");
  }
  if (!looksLikeStaticImageUrl(tileSource)) {
    return `${String(tileSource).replace(/\/+$/, "")}/3/3/3.png`;
  }
  return null;
}

function MediaThumb({ media, is3d }) {
  const [failed, setFailed] = useState(false);
  const thumb = resolveLibraryThumb(media);

  if (thumb && !failed) {
    return (
      <Box
        component="img"
        src={thumb}
        alt=""
        onError={() => setFailed(true)}
        sx={{ width: 44, height: 44, objectFit: "cover", display: "block" }}
      />
    );
  }
  if (is3d) {
    return <ViewInArRoundedIcon sx={{ fontSize: 22, color: AI_ASSISTANT_BLUE }} />;
  }
  return <ImageOutlinedIcon sx={{ fontSize: 22, color: "#059669" }} />;
}

MediaThumb.propTypes = {
  media: PropTypes.object,
  is3d: PropTypes.bool,
};

function MediaSlideRow({ slide, index, disabled, onRemove }) {
  const media = slide?.related_3d || {};
  const is3d = Boolean(media.modelUrl) || media.kind === "3d";

  return (
    <Box
      sx={{
        p: 1.2,
        borderRadius: 2,
        bgcolor: "#ffffff",
        border: "1px solid rgba(0, 97, 254, 0.22)",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            flexShrink: 0,
            width: 26,
            height: 26,
            borderRadius: "50%",
            bgcolor: "rgba(0, 97, 254, 0.1)",
            color: AI_ASSISTANT_BLUE,
            display: "grid",
            placeItems: "center",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          {index + 1}
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            width: 44,
            height: 44,
            borderRadius: 1.5,
            overflow: "hidden",
            bgcolor: "rgba(15,23,42,0.06)",
            display: "grid",
            placeItems: "center",
          }}
        >
          <MediaThumb media={media} is3d={is3d} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              display: "inline-block",
              px: 0.8,
              py: 0.15,
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 0.4,
              color: is3d ? "#1e3a8a" : "#065f46",
              bgcolor: is3d ? "rgba(0,97,254,0.12)" : "rgba(16,185,129,0.14)",
            }}
          >
            {is3d ? "3D" : "SLIDE"}
          </Typography>
          <Typography
            fontWeight={700}
            sx={{ mt: 0.35, fontSize: 13.5, color: "#0f172a", wordBreak: "break-word" }}
          >
            {slide?.title || media.label || `Slide ${index + 1}`}
          </Typography>
          <Typography variant="caption" sx={{ color: "#64748b" }}>
            Separate library slide — delete to remove from the flow
          </Typography>
        </Box>
        <IconButton
          size="small"
          disabled={disabled}
          aria-label="Delete library slide"
          onClick={() => onRemove?.(index)}
          sx={{ color: "#b91c1c", flexShrink: 0 }}
        >
          <DeleteOutlineRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

MediaSlideRow.propTypes = {
  slide: PropTypes.object,
  index: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  onRemove: PropTypes.func,
};

function ContentSlideRow({ slide, index, disabled, onSave, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(slide?.title || "");
  const [bulletsText, setBulletsText] = useState(bulletsToText(slide?.bullets));

  useEffect(() => {
    if (!editing) {
      setTitle(slide?.title || "");
      setBulletsText(bulletsToText(slide?.bullets));
    }
  }, [slide?.title, slide?.bullets, editing]);

  const handleSave = () => {
    onSave?.(index, {
      title: title.trim() || slide.title,
      bullets: textToBullets(bulletsText),
    });
    setEditing(false);
  };

  return (
    <Box
      sx={{
        p: 1.2,
        borderRadius: 2,
        bgcolor: "#ffffff",
        border: "1px solid rgba(148,163,184,0.28)",
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box
          sx={{
            flexShrink: 0,
            width: 26,
            height: 26,
            borderRadius: "50%",
            bgcolor: "rgba(0, 97, 254, 0.1)",
            color: AI_ASSISTANT_BLUE,
            display: "grid",
            placeItems: "center",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          {index + 1}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {editing ? (
            <Stack spacing={1}>
              <TextField
                size="small"
                label="Slide title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={disabled}
                fullWidth
              />
              <TextField
                size="small"
                label="Bullets (one per line)"
                value={bulletsText}
                onChange={(e) => setBulletsText(e.target.value)}
                disabled={disabled}
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
              />
              <Stack direction="row" spacing={0.8}>
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  startIcon={<CheckRoundedIcon />}
                  disabled={disabled}
                  onClick={handleSave}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    bgcolor: `${AI_ASSISTANT_BLUE} !important`,
                    color: "#fff !important",
                  }}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  variant="text"
                  disabled={disabled}
                  onClick={() => setEditing(false)}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          ) : (
            <>
              <Typography
                fontWeight={700}
                sx={{ fontSize: 13.5, color: "#0f172a", wordBreak: "break-word" }}
              >
                {slide?.title || `Slide ${index + 1}`}
              </Typography>
              <Box component="ul" sx={{ m: "6px 0 0", pl: 2.2, color: "#334155" }}>
                {(slide?.bullets || []).slice(0, 6).map((b, i) => (
                  <Typography
                    component="li"
                    key={`${index}-b-${i}`}
                    variant="body2"
                    sx={{ fontSize: 12.5, mb: 0.35, wordBreak: "break-word" }}
                  >
                    {b}
                  </Typography>
                ))}
              </Box>
            </>
          )}
        </Box>
        {!editing ? (
          <Stack direction="row" spacing={0.2} sx={{ flexShrink: 0 }}>
            <IconButton
              size="small"
              disabled={disabled}
              aria-label="Edit slide"
              onClick={() => setEditing(true)}
              sx={{ color: AI_ASSISTANT_BLUE }}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={disabled}
              aria-label="Delete slide"
              onClick={() => onRemove?.(index)}
              sx={{ color: "#b91c1c" }}
            >
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}

ContentSlideRow.propTypes = {
  slide: PropTypes.object,
  index: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  onSave: PropTypes.func,
  onRemove: PropTypes.func,
};

export default function PresentationDraftCard({
  pending,
  onConfirm,
  onDiscard,
  onUpdateSlide,
  onRemoveSlide,
  onSearchLibrary,
  onAddLibrarySlide,
  confirming,
  disabled,
}) {
  const [libQuery, setLibQuery] = useState("");
  const [libBusy, setLibBusy] = useState(false);
  const [libResults, setLibResults] = useState([]);
  const [libError, setLibError] = useState("");

  if (!pending?.slides?.length) return null;

  const slides = pending.slides;
  const mediaCount = slides.filter(isMediaSlide).length;
  const modeNote =
    pending.route_mode === "apply_to_current"
      ? "Slides will be added to the presentation you have open."
      : "On confirm I’ll create a new presentation and open the maker.";

  const handleSearch = async () => {
    const q = libQuery.trim();
    if (!q || typeof onSearchLibrary !== "function") return;
    setLibBusy(true);
    setLibError("");
    try {
      const results = await onSearchLibrary(q);
      setLibResults(Array.isArray(results) ? results : []);
      if (!results?.length) setLibError("No English library matches found.");
    } catch (err) {
      setLibError(err?.message || "Library search failed.");
      setLibResults([]);
    } finally {
      setLibBusy(false);
    }
  };

  const handleAdd = async (item) => {
    if (!item || typeof onAddLibrarySlide !== "function") return;
    setLibBusy(true);
    setLibError("");
    try {
      await onAddLibrarySlide(item);
      setLibResults((prev) =>
        prev.filter(
          (r) =>
            (r.modelUrl || r.imageUrl || r.label) !==
            (item.modelUrl || item.imageUrl || item.label)
        )
      );
    } catch (err) {
      setLibError(err?.message || "Could not add library slide.");
    } finally {
      setLibBusy(false);
    }
  };

  return (
    <Box
      sx={{
        mx: 2,
        mb: 1.5,
        p: 1.6,
        borderRadius: 2.5,
        border: "1px solid rgba(0, 97, 254, 0.25)",
        bgcolor: "#f8fbff",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        maxHeight: 420,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 800, color: AI_ASSISTANT_BLUE, letterSpacing: 0.6 }}>
        PRESENTATION DRAFT
      </Typography>
      <Typography fontWeight={700} sx={{ mt: 0.4, color: "#0f172a", wordBreak: "break-word" }}>
        {pending.presentation_title || pending.topic || "Untitled presentation"}
      </Typography>
      <Typography variant="body2" sx={{ color: "#64748b", mt: 0.3 }}>
        {slides.length} slide{slides.length === 1 ? "" : "s"}
        {mediaCount ? ` · ${mediaCount} library media` : ""}
      </Typography>
      <Typography variant="caption" sx={{ display: "block", mt: 0.6, color: "#64748b" }}>
        {modeNote} Each 3D/library item is its own numbered slide — delete any, or add from
        your English library below.
      </Typography>

      <Divider sx={{ my: 1.2 }} />

      <Stack
        spacing={1}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          pr: 0.4,
        }}
      >
        {slides.map((slide, index) =>
          isMediaSlide(slide) ? (
            <MediaSlideRow
              key={slide.id || `media-${index}`}
              slide={slide}
              index={index}
              disabled={disabled || confirming}
              onRemove={onRemoveSlide}
            />
          ) : (
            <ContentSlideRow
              key={slide.id || `content-${index}`}
              slide={slide}
              index={index}
              disabled={disabled || confirming}
              onSave={onUpdateSlide}
              onRemove={onRemoveSlide}
            />
          )
        )}
      </Stack>

      {typeof onSearchLibrary === "function" ? (
        <Box sx={{ mt: 1.2, pt: 1, borderTop: "1px solid rgba(148,163,184,0.22)" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#64748b", mb: 0.7 }}>
            ADD FROM LIBRARY (ENGLISH)
          </Typography>
          <Stack direction="row" spacing={0.8} alignItems="center">
            <TextField
              size="small"
              fullWidth
              placeholder="Search fibroblasts, adipose, cartilage…"
              value={libQuery}
              disabled={disabled || confirming || libBusy}
              onChange={(e) => setLibQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
            />
            <Button
              size="small"
              variant="outlined"
              disabled={disabled || confirming || libBusy || !libQuery.trim()}
              onClick={handleSearch}
              startIcon={
                libBusy ? <CircularProgress size={14} /> : <SearchRoundedIcon fontSize="small" />
              }
              sx={{
                textTransform: "none",
                fontWeight: 700,
                flexShrink: 0,
                color: `${AI_ASSISTANT_BLUE} !important`,
                borderColor: `${AI_ASSISTANT_BLUE} !important`,
              }}
            >
              Search
            </Button>
          </Stack>
          {libError ? (
            <Typography variant="caption" sx={{ color: "#b91c1c", mt: 0.6, display: "block" }}>
              {libError}
            </Typography>
          ) : null}
          {libResults.length ? (
            <Stack spacing={0.6} sx={{ mt: 0.9 }}>
              {libResults.slice(0, 6).map((item) => {
                const is3d = Boolean(item.modelUrl) || item.kind === "3d";
                return (
                  <Stack
                    key={`${item.slide_id || item.title_id || item.label}-${item.modelUrl || item.imageUrl}`}
                    direction="row"
                    spacing={0.8}
                    alignItems="center"
                    sx={{
                      p: 0.7,
                      borderRadius: 1.5,
                      border: "1px solid rgba(148,163,184,0.28)",
                      bgcolor: "#fff",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 800,
                        px: 0.7,
                        borderRadius: 999,
                        color: is3d ? "#1e3a8a" : "#065f46",
                        bgcolor: is3d ? "rgba(0,97,254,0.12)" : "rgba(16,185,129,0.14)",
                      }}
                    >
                      {is3d ? "3D" : "SLIDE"}
                    </Typography>
                    <Typography
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 12.5,
                        fontWeight: 600,
                        wordBreak: "break-word",
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<AddRoundedIcon fontSize="small" />}
                      disabled={disabled || confirming || libBusy}
                      onClick={() => handleAdd(item)}
                      sx={{ textTransform: "none", fontWeight: 700, flexShrink: 0 }}
                    >
                      Add
                    </Button>
                  </Stack>
                );
              })}
            </Stack>
          ) : null}
        </Box>
      ) : null}

      <Stack direction="row" spacing={1} sx={{ mt: 1.4, flexShrink: 0 }}>
        <Button
          variant="contained"
          disableElevation
          disabled={disabled || confirming}
          onClick={onConfirm}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            color: "#ffffff !important",
            bgcolor: `${AI_ASSISTANT_BLUE} !important`,
            "&:hover": { bgcolor: "#0052d6 !important" },
            "&.Mui-disabled": {
              color: "rgba(255,255,255,0.85) !important",
              bgcolor: "rgba(0, 97, 254, 0.45) !important",
            },
          }}
        >
          {confirming ? "Creating…" : "Make presentation"}
        </Button>
        <Button
          variant="outlined"
          disabled={confirming}
          onClick={onDiscard}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            color: `${AI_ASSISTANT_BLUE} !important`,
            borderColor: `${AI_ASSISTANT_BLUE} !important`,
            bgcolor: "#ffffff !important",
          }}
        >
          Discard
        </Button>
      </Stack>
    </Box>
  );
}

PresentationDraftCard.propTypes = {
  pending: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
  onDiscard: PropTypes.func.isRequired,
  onUpdateSlide: PropTypes.func.isRequired,
  onRemoveSlide: PropTypes.func.isRequired,
  onSearchLibrary: PropTypes.func,
  onAddLibrarySlide: PropTypes.func,
  confirming: PropTypes.bool,
  disabled: PropTypes.bool,
};
